<template>
  <div class="mb-3" data-cy="team-settings-section">
    <div class="mb-1 flex items-center justify-between">
      <h3 class="text-lg font-bold">{{ $t("team.title") }}</h3>
      <SButton
        type="button"
        :disabled="!isOnline"
        data-cy="team-create-user-button"
        @click="openCreate"
      >
        <PlusIcon class="mr-2 h-4 w-4" />
        {{ $t("team.create-user") }}
      </SButton>
    </div>

    <!-- Offline notice — team changes sync to the account, so writes need a connection. -->
    <div
      v-if="!isOnline"
      class="mb-2 flex items-start gap-2 rounded-md bg-slate-800 px-3 py-2 text-sm text-white"
      data-cy="team-offline-banner"
    >
      <InfoIcon class="mt-0.5 h-4 w-4 shrink-0" />
      <p>{{ $t("team.offline-banner") }}</p>
    </div>

    <SDataTable
      v-if="members.length || isLoading"
      :table="table"
      :loading="isLoading"
      data-cy="team-table"
    />
    <div
      v-else
      class="py-6 text-center text-muted-foreground"
      data-cy="team-empty"
    >
      {{ isOnline ? $t("team.no-members") : $t("team.offline-empty") }}
    </div>

    <TeamMemberDialog
      v-if="memberDialogOpen"
      :key="editingMember ? `edit-${editingMember.roleId}` : 'create'"
      :member="editingMember"
      :members="members"
      :role-labels="roleLabels"
      :group-id="groupId"
      @close="memberDialogOpen = false"
      @saved="onSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
} from "@tanstack/vue-table";

import { SButton } from "@smeetz/ui-core";
import { SDataTable } from "@smeetz/ui-components/s-data-table";
import { InfoIcon, PlusIcon } from "@smeetz/ui-icons";

import TeamMemberDialog from "./TeamMemberDialog.vue";
import TeamNameCell from "./cells/TeamNameCell.vue";
import TeamActionsCell from "./cells/TeamActionsCell.vue";

import {
  fetchGroupRoles,
  getTeamMembers,
  removeTeamMember,
} from "@/api/team/team";
import { MEMBER_STATE_CONFIRMED, POS_PIN_ROLE_TYPES } from "@/constants/team";
import { UserRole } from "@/interfaces/store/IUserState";
import { ITeamMember } from "@/interfaces/responses/team";
import UserModule from "@/store/modules/user";
import NotificationService from "@/services/notification.service";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import { getTeamGroupId, mapTeamMember } from "@/utils/helpers/team";

const { t } = useI18n({ useScope: "global" });

// ---------- State ----------
const isLoading = ref(false);
const members = ref<ITeamMember[]>([]);
const roleLabels = ref<Record<number, string>>({});
const memberDialogOpen = ref(false);
const editingMember = ref<ITeamMember | null>(null);

// Team management is account data: reads show the last fetch, writes are
// blocked while offline (see the banner) — same pattern as cash transfers.
const isOnline = ref(
  typeof navigator !== "undefined" ? navigator.onLine : true
);

function handleOnline(): void {
  isOnline.value = true;
}

function handleOffline(): void {
  isOnline.value = false;
}

// The till's group (selected config), NOT the user's first group — a
// multi-group user must manage exactly the account this POS operates.
const groupId = computed(() => getTeamGroupId() || 0);

// ---------- Computed ----------
const ownersCount = computed(
  () =>
    members.value.filter((member) => member.roleType === UserRole.ROLE_OWNER)
      .length
);

// ---------- Table ----------
const columnHelper = createColumnHelper<ITeamMember>();

const columns = [
  columnHelper.accessor("fullName", {
    id: "fullName",
    header: () => String(t("team.column-name")),
    enableSorting: true,
    sortingFn: "alphanumeric",
    minSize: 220,
    cell: ({ row }) =>
      h(TeamNameCell, {
        member: row.original,
        isSelf: isSelf(row.original),
      }),
  }),
  columnHelper.accessor("roleName", {
    id: "roleName",
    header: () => String(t("team.column-role")),
    enableSorting: true,
    sortingFn: "alphanumeric",
    minSize: 140,
  }),
  columnHelper.accessor("state", {
    id: "state",
    header: () => String(t("team.column-status")),
    enableSorting: false,
    minSize: 110,
    // Dot + plain label, matching the prototype (● Confirmed).
    cell: ({ row }) => {
      const confirmed = row.original.state === MEMBER_STATE_CONFIRMED;

      return h(
        "span",
        {
          class: "inline-flex items-center gap-1.5",
          "data-cy": "team-status-badge",
        },
        [
          h("span", {
            class: `h-2 w-2 shrink-0 rounded-full ${
              confirmed ? "bg-green-500" : "bg-amber-500"
            }`,
            "aria-hidden": "true",
          }),
          String(
            confirmed ? t("team.status-confirmed") : t("team.status-pending")
          ),
        ]
      );
    },
  }),
  columnHelper.display({
    id: "actions",
    header: () => "",
    minSize: 100,
    cell: ({ row }) =>
      h(TeamActionsCell, {
        // PIN members (POS Seller / POS Seller Manager) are managed from the
        // back office: their role is immutable and they carry no email/SSN.
        editDisabled:
          !isOnline.value || POS_PIN_ROLE_TYPES.includes(row.original.roleType),
        editTitle: POS_PIN_ROLE_TYPES.includes(row.original.roleType)
          ? String(t("team.pin-user-locked"))
          : String(t("team.edit-user")),
        removeDisabled: !isOnline.value,
        onEdit: () => openEdit(row.original),
        onRemove: () => onRemove(row.original),
      }),
  }),
];

const table = useVueTable({
  get data() {
    return members.value;
  },
  columns,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

// ---------- Handlers ----------
function openCreate(): void {
  editingMember.value = null;
  memberDialogOpen.value = true;
}

function openEdit(member: ITeamMember): void {
  if (!isOnline.value || POS_PIN_ROLE_TYPES.includes(member.roleType)) {
    return;
  }

  editingMember.value = member;
  memberDialogOpen.value = true;
}

async function onRemove(member: ITeamMember): Promise<void> {
  if (!isOnline.value) {
    return;
  }

  // Guardrails fail loudly with a reason rather than silently.
  if (isSelf(member)) {
    NotificationService.showCustomMessage(
      t("team.cannot-remove-self"),
      "warning"
    );

    return;
  }
  if (member.roleType === UserRole.ROLE_OWNER && ownersCount.value === 1) {
    NotificationService.showCustomMessage(
      t("team.last-owner-warning"),
      "warning"
    );

    return;
  }

  const confirmed = await showConfirm({
    title: t("team.remove-user"),
    message: t("team.remove-confirm-message"),
    highlight: member.fullName || member.email,
    variant: "destructive",
    confirmText: t("team.remove"),
  });

  if (!confirmed) {
    return;
  }

  isLoading.value = true;

  try {
    await removeTeamMember({
      groupId: groupId.value,
      roleId: member.roleId,
    });
    NotificationService.showCustomSuccess(t("team.member-removed"));
    await fetchMembers();
  } catch (error) {
    NotificationService.showCustomError(
      handleErrorAndPrepareMessage(error).errorMessage
    );
  } finally {
    isLoading.value = false;
  }
}

async function onSaved(): Promise<void> {
  memberDialogOpen.value = false;
  await fetchMembers();
}

// ---------- Helpers ----------
function isSelf(member: ITeamMember): boolean {
  return member.userId !== null && member.userId === UserModule.getUserId;
}

async function fetchMembers(): Promise<void> {
  if (!groupId.value || !isOnline.value) {
    return;
  }

  isLoading.value = true;

  try {
    const response = await getTeamMembers(groupId.value);

    members.value = (response.data?.userGroups || []).map(mapTeamMember);
  } catch (error) {
    NotificationService.showCustomError(
      handleErrorAndPrepareMessage(error).errorMessage
    );
  } finally {
    isLoading.value = false;
  }
}

async function fetchRoleLabels(): Promise<void> {
  try {
    const response = await fetchGroupRoles();

    roleLabels.value = response.data?.roles || {};
  } catch {
    // The dialog falls back to the local English role labels.
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  fetchMembers();
  fetchRoleLabels();
});

onBeforeUnmount(() => {
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
});
</script>
